import type {
  AccountType,
  CoaVersionStatus,
  NaturalBalance,
  ResourceAccessMode,
  RollupCategory,
  RowStatus,
  VersionMappingType,
} from '../generated/enums.js';

/** Income-statement debit types: cost (COGS) and operating/other expenses. */
export const INCOME_STATEMENT_DEBIT_ACCOUNT_TYPES = [
  'COGS',
  'EXPENSE',
  'OTHER_EXPENSE',
] as const satisfies readonly AccountType[];

export function isIncomeStatementDebitAccountType(
  accountType: AccountType,
): accountType is (typeof INCOME_STATEMENT_DEBIT_ACCOUNT_TYPES)[number] {
  return (INCOME_STATEMENT_DEBIT_ACCOUNT_TYPES as readonly AccountType[]).includes(accountType);
}

export interface GLAccount {
  accountId: string;
  code: string;
  name: string;
  type: string;
  category?: string;
  isPostable: boolean;
  active: boolean;
  parentAccountId?: string;
  children?: GLAccount[];
}

export interface GLTransaction {
  transactionId: string;
  date: string;
  description: string;
  amount: number;
  accountId: string;
  account?: GLAccount;
}

// CoA Versions – wire contracts

export interface CoaVersionResponse {
  versionId: string;
  versionNumber: string;
  name: string;
  /**
   * Accounting framework this chart is written for — descriptive, not a gate. One version
   * may legitimately be shared across books of different frameworks, so a divergence at
   * activation is only the `COA_FRAMEWORK_MISMATCH` warning; the blocking rule is
   * `STAGED_TAX_RAIL_FRAMEWORK_MISMATCH`.
   */
  frameworkCode: string;
  description?: string | null;
  status: CoaVersionStatus;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  scheduledGoLiveDate?: string | null;
  publishedAt?: string | null;
  publishedBy?: string | null;
  deprecatedAt?: string | null;
  deprecatedBy?: string | null;
  parentVersionId?: string | null;
  checksum?: string | null;
  validationErrorsJson?: string | null;
  notes?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export interface ListCoaVersionsQuery {
  status?: CoaVersionStatus;
}

/** How `POST /gl/coa-versions/setup` should provision the (entity, book) scope's active version. */
export const COA_SETUP_MODES = ['CREATE_NEW', 'USE_EXISTING', 'CLONE_EXISTING'] as const;
export type CoaSetupMode = (typeof COA_SETUP_MODES)[number];

/**
 * Request body for `POST /gl/coa-versions/setup`, which orchestrates the full
 * draft -> publish -> activate sequence for an (entity, book) scope in one call.
 */
export interface SetupCoaScopeRequest {
  entityId: string;
  bookId: string;
  mode: CoaSetupMode;
  /** Required for CREATE_NEW and CLONE_EXISTING. */
  versionNumber?: string;
  /** Required for CREATE_NEW and CLONE_EXISTING. */
  name?: string;
  description?: string;
  notes?: string;
  /** Required for CLONE_EXISTING. */
  sourceVersionId?: string;
  /** Required for USE_EXISTING. */
  versionId?: string;
}

// GL Accounts – wire contracts

/**
 * An account's role inside a managed tax-liability rail (ALC-1541 / ALC-1551).
 *
 * Derived from `TaxLiabilityRail` at read time — there is no column on `Account`. A `PARENT` is
 * the non-postable managed head the system routes postings *within*; `APURADO` and
 * `GUIDES_PAYABLE` are its system-owned stages and are never directly selectable as a slot
 * mapping; `LEGADO` is the pre-feature postable account reparented under the rail.
 */
export type AccountRailRole = 'PARENT' | 'APURADO' | 'GUIDES_PAYABLE' | 'LEGADO';

export interface AccountResponse {
  glAccountId: string;
  versionId: string;
  code: string;
  name: string;
  description?: string | null;
  accountType: AccountType;
  naturalBalance: NaturalBalance;
  isContra: boolean;
  contraOfAccountId?: string | null;
  isPostable: boolean;
  /** Defaults to false for responses from older API versions. */
  isConsolidationElimination?: boolean;
  parentAccountId?: string | null;
  displayOrder?: number | null;
  isReconcilable?: boolean | null;
  /** Defaults to INHERIT_RBAC for records returned during an in-flight rollout. */
  accessMode?: ResourceAccessMode;
  externalId?: string | null;
  aliasesJson?: string | null;
  tagsJson?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  status: RowStatus;
  createdAt: string;
  /** Derived per CoA version from `TaxLiabilityRail`; null when this account is not part of a rail. */
  railRole?: AccountRailRole | null;
  /** `TaxAccountSlot.code` of the rail that owns this account, when `railRole` is set. */
  railSlotCode?: string | null;
  /** All tax memberships sharing this physical rail; singular code remains for older clients. */
  railSlotCodes?: string[];
}

export interface GlAccountAccessMember {
  organizationMembershipId: string;
  userId: string;
  name: string;
  email: string;
  roleSlugs: string[];
  hasAccess: boolean;
  hasDirectAccess: boolean;
  groupNames: string[];
}

export interface GlAccountAccessGroup {
  groupId: string;
  name: string;
  description: string | null;
  hasAccess: boolean;
}

export interface GlAccountAccessResponse {
  accountId: string;
  accessMode: ResourceAccessMode;
  members: GlAccountAccessMember[];
  groups: GlAccountAccessGroup[];
}

export interface CreateAccountRequest {
  versionId: string;
  code: string;
  name: string;
  description?: string;
  accountType: AccountType;
  naturalBalance: NaturalBalance;
  isPostable?: boolean;
  isConsolidationElimination?: boolean;
  parentAccountId?: string | null;
  isContra?: boolean;
  contraOfAccountId?: string | null;
  isReconcilable?: boolean;
  tagsJson?: string;
  externalId?: string;
  aliasesJson?: string;
  displayOrder?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
}

export interface CreateAccountInActiveVersionRequest {
  versionId?: string | null;
  entityId?: string | null;
  bookId?: string | null;
  code: string;
  name: string;
  description?: string;
  accountType: AccountType;
  naturalBalance: NaturalBalance;
  isPostable?: boolean;
  isConsolidationElimination?: boolean;
  parentAccountId?: string | null;
  isContra?: boolean;
  contraOfAccountId?: string | null;
  isReconcilable?: boolean;
  tagsJson?: string;
  externalId?: string;
  aliasesJson?: string;
  displayOrder?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
}

export interface UpdateAccountRequest {
  code?: string;
  name?: string;
  description?: string;
  accountType?: AccountType;
  naturalBalance?: NaturalBalance;
  isPostable?: boolean;
  parentAccountId?: string | null;
  isContra?: boolean;
  contraOfAccountId?: string | null;
  isReconcilable?: boolean;
  tagsJson?: string;
  externalId?: string;
  aliasesJson?: string;
  displayOrder?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  status?: RowStatus;
}

export interface ListAccountsQuery {
  versionId: string;
  entityId?: string;
  status?: RowStatus;
  /** Inclusive lower bound on account `createdAt` (YYYY-MM-DD) */
  createdFrom?: string;
  /** Inclusive upper bound on account `createdAt` (YYYY-MM-DD) */
  createdTo?: string;
}

/** The server-resolved accounting scope that an account picker is operating in. */
export interface ResolvedGlAccountScope {
  entityId?: string;
  bookId?: string;
  coaVersionId: string;
  /** Human-readable chart name, provided so selectors can make the active chart visible. */
  coaVersionName: string;
  frameworkCode: string;
}

/**
 * Query for the scoped account response. Supplying `entityId` resolves the posting scope;
 * `versionId` is valid only together with `mode: 'COA_ADMIN'`.
 */
export interface ScopedGlAccountsQuery {
  entityId?: string;
  bookId?: string;
  accountingDate?: string;
  versionId?: string;
  mode?: 'COA_ADMIN';
  status?: RowStatus;
  postable?: boolean;
  accountTypes?: AccountType[];
}

export interface ScopedGlAccountsResponse {
  scope: ResolvedGlAccountScope;
  accounts: AccountResponse[];
}

/** Query for the server-projected chart-of-accounts view. */
export interface CoaPresetViewQuery {
  versionId: string;
  entityId: string;
  /** Preset UUID, `none` for the unmodified chart, or omitted for the chart default. */
  presetId?: string;
}

export interface CoaPresetViewAccount {
  glAccountId: string;
  foldedIntoAccountId: string | null;
  hasPostings: boolean;
  hiddenByPreset: boolean;
  /** Non-zero only for an account that is the folded root of generated children. */
  foldedChildCount: number;
}

export interface CoaPresetViewResponse {
  accounts: CoaPresetViewAccount[];
  totalAccounts: number;
  hiddenCount: number;
  foldedCount: number;
  /** The preset selected by the explicit/default/sentinel resolution, or null for `none`. */
  presetId: string | null;
}

export type { AccountType, NaturalBalance } from '../generated/enums.js';

// GL Rollup Groups – wire contracts

export interface RollupGroupResponse {
  rollupGroupId: string;
  code: string;
  name: string;
  category: RollupCategory;
  parentGroupId?: string | null;
  displayOrder: number;
  isSubtotal: boolean;
  description?: string | null;
  status: RowStatus;
}

export interface CreateRollupGroupRequest {
  code: string;
  name: string;
  category: RollupCategory;
  parentGroupId?: string | null;
  displayOrder?: number;
  isSubtotal?: boolean;
  description?: string;
  status?: RowStatus;
}

export type UpdateRollupGroupRequest = Partial<CreateRollupGroupRequest>;

export interface ListRollupGroupsQuery {
  category?: RollupCategory;
  status?: RowStatus;
}

// GL Account Rollup Maps – wire contracts

export interface AccountRollupMapResponse {
  mapId: string;
  versionId: string;
  glAccountId: string;
  rollupGroupId: string;
  rollupGroup?: {
    rollupGroupId: string;
    name: string;
    category: RollupCategory;
  };
}

export interface BulkUpsertAccountRollupMapsRequest {
  versionId: string;
  mappings: {
    glAccountId: string;
    rollupGroupId?: string | null;
  }[];
}

export interface ListAccountRollupMapsQuery {
  versionId: string;
  glAccountId?: string;
  rollupGroupId?: string;
}

// GL Version-to-Version Account Maps (Crosswalks) – wire contracts

export interface VersionAccountMapResponse {
  mapId: string;
  fromVersionId: string;
  toVersionId: string;
  fromAccountId?: string | null;
  toAccountId?: string | null;
  mappingType: VersionMappingType;
  justification?: string | null;
  createdBy?: string | null;
  createdAt: string;
}

export interface BulkUpsertVersionAccountMapsRequest {
  fromVersionId: string;
  toVersionId: string;
  mappings: {
    fromAccountId?: string | null;
    toAccountId?: string | null;
    mappingType: VersionMappingType;
    justification?: string | null;
  }[];
}

export interface ListVersionAccountMapsQuery {
  fromVersionId: string;
  toVersionId: string;
  fromAccountId?: string;
  toAccountId?: string;
  mappingType?: VersionMappingType;
}

// Bulk create accounts from AI suggestions

export interface BulkCreateFromSuggestionsRequest {
  versionId?: string | null;
  entityId?: string | null;
  bookId?: string | null;
  suggestions: {
    slotId: string;
    name: string;
    code: string;
    accountType: AccountType;
    naturalBalance: NaturalBalance;
    isPostable: boolean;
    parentCode: string | null;
  }[];
}

export interface BulkCreateFromSuggestionsResponse {
  created: {
    slotId: string;
    glAccountId: string;
  }[];
}

// CoA version assignment (per entity/book activation binding) – wire contract

export interface CoaVersionAssignmentResponse {
  assignmentId: string;
  entityId: string;
  bookId: string;
  versionId: string;
  status: 'SCHEDULED' | 'ACTIVE' | 'SUPERSEDED';
  scheduledGoLiveDate?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  appliedAt?: string | null;
  appliedBy?: string | null;
  supersededAt?: string | null;
  notes?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

// CoA activation impact analysis – wire contracts

/**
 * A single blocking issue or warning surfaced by the activation impact analysis.
 * `code` is a stable machine-readable identifier; `message` is human-readable.
 */
export interface ActivationIssue {
  code: string;
  message: string;
  accountCodes?: string[];
  periodIds?: string[];
}

/**
 * Result of analyzing the activation of a CoA version for an (entity, book) scope.
 * Consumed by the activation-preview endpoint and the activation guard.
 */
export interface ActivationImpactReport {
  tenantId: string;
  entityId: string;
  bookId: string;
  targetVersionId: string;
  /** Version currently active for the scope assignment, null if none. */
  currentVersionId: string | null;
  /** True when there are no blocking issues. */
  canActivate: boolean;
  /** Earliest eligible go-live date (first day of an empty OPEN period), if derivable. */
  suggestedGoLiveDate: string | null;
  blocking: ActivationIssue[];
  warnings: ActivationIssue[];
  periods: {
    closed: number;
    openEmpty: number;
    openPosted: number;
    /** Empty OPEN periods that would be re-stamped to the new version on activation. */
    toRestampPeriodIds: string[];
    /** Periods kept on their current version (have postings — immutable history). */
    keptPeriodIds: string[];
  };
  crosswalk: {
    coverageComplete: boolean;
    unmappedAccountCodes: string[];
  };
}
